/**
 * "Forgot password?": email a reset link. The reply is the same whether or
 * not the address has an account, so the form can't be used to find out.
 * Supabase rate-limits reset emails per account, so its email rate-limit
 * error only happens for an address that has one: it gets the same reply.
 */

import { useState, type FormEvent } from 'react';
import { sendPasswordResetEmail } from '@shared/auth/accountSecurity';
import { passwordErrorMessage } from '@shared/auth/passwordErrors';
import {
  AUTH_BODY_CLASS,
  AUTH_ERROR_CLASS,
  AUTH_HEADING_CLASS,
  AUTH_INPUT_CLASS,
  AUTH_LABEL_CLASS,
  AUTH_LINK_BUTTON_CLASS,
  AUTH_SUBMIT_CLASS,
  AUTH_SUBMIT_STYLE,
  AUTH_SUCCESS_CLASS,
} from './authFormStyles';

/** Supabase's per-account email rate limit (only an existing account reaches it). */
const EMAIL_RATE_LIMIT_CODE = 'over_email_send_rate_limit';

function isEmailRateLimit(error: unknown): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    'code' in error &&
    error.code === EMAIL_RATE_LIMIT_CODE
  );
}

interface ForgotPasswordFormProps {
  initialEmail: string;
  onBack: () => void;
}

export function ForgotPasswordForm({ initialEmail, onBack }: ForgotPasswordFormProps) {
  const [email, setEmail] = useState(initialEmail);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [isSending, setIsSending] = useState(false);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError('');
    setIsSending(true);
    try {
      await sendPasswordResetEmail(email);
      setSentTo(email);
    } catch (err: unknown) {
      if (isEmailRateLimit(err)) {
        setSentTo(email);
      } else {
        setError(passwordErrorMessage(err));
      }
    } finally {
      setIsSending(false);
    }
  }

  return (
    <div className="w-full">
      <h2 className={AUTH_HEADING_CLASS}>Reset your password</h2>

      {sentTo ? (
        <p role="status" className={AUTH_SUCCESS_CLASS}>
          If an account exists for {sentTo}, we&apos;ve sent it a link to set a new password. The
          link works once and expires soon.
        </p>
      ) : (
        <form onSubmit={(e) => void handleSubmit(e)} className="w-full">
          <p className={AUTH_BODY_CLASS}>
            Enter your email and we&apos;ll send you a link to set a new password.
          </p>
          <div className="mb-4">
            <label htmlFor="reset-email" className={AUTH_LABEL_CLASS}>
              Email
            </label>
            <input
              id="reset-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoFocus
              placeholder="you@example.com"
              autoComplete="username"
              className={AUTH_INPUT_CLASS}
            />
          </div>

          {error && (
            <div role="alert" className={AUTH_ERROR_CLASS}>
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={isSending}
            className={AUTH_SUBMIT_CLASS}
            style={AUTH_SUBMIT_STYLE}
          >
            {isSending ? 'Sending...' : 'Send reset link'}
          </button>
        </form>
      )}

      <button type="button" onClick={onBack} className={`${AUTH_LINK_BUTTON_CLASS} mt-4`}>
        Back to sign in
      </button>
    </div>
  );
}
